/**
 * Settings Store
 * Управление настройками пользователя с сохранением в localStorage
 */
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { setupTheme, useTheme } from '@vinprop/design-system'
import type { UserSettings, NotificationSettings, DisplaySettings } from '../types/settings.types'
import { Language, Theme } from '../types/settings.types'

const { setPreference } = useTheme()

const SETTINGS_STORAGE_KEY = 'crm-user-settings'

// Дефолтные настройки
const DEFAULT_SETTINGS: UserSettings = {
  notifications: {
    emailNotifications: true,
    pushNotifications: false,
    dealUpdates: true,
    clientUpdates: true,
    systemUpdates: true,
  },
  display: {
    language: Language.RU,
    theme: Theme.LIGHT,
    itemsPerPage: 20,
    showHints: true,
    onboardingSeen: {},
  },
}

export const useSettingsStore = defineStore('settings', () => {
  // State
  const settings = ref<UserSettings>(loadSettings())
  const isLoading = ref(false)

  // Helpers
  function loadSettings(): UserSettings {
    try {
      const stored = localStorage.getItem(SETTINGS_STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        // Merge with defaults to ensure all fields exist
        return {
          notifications: { ...DEFAULT_SETTINGS.notifications, ...parsed.notifications },
          display: { ...DEFAULT_SETTINGS.display, ...parsed.display },
        }
      }
    } catch (error) {
      console.error('Failed to load settings:', error)
    }
    return DEFAULT_SETTINGS
  }

  function saveSettings() {
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings.value))
    } catch (error) {
      console.error('Failed to save settings:', error)
    }
  }

  // Watch for changes and auto-save
  watch(
    settings,
    () => {
      saveSettings()
    },
    { deep: true },
  )

  // Actions

  /**
   * Обновить настройки уведомлений
   */
  const updateNotificationSettings = (newSettings: Partial<NotificationSettings>) => {
    settings.value.notifications = {
      ...settings.value.notifications,
      ...newSettings,
    }
  }

  /**
   * Обновить настройки отображения
   */
  const updateDisplaySettings = (newSettings: Partial<DisplaySettings>) => {
    settings.value.display = {
      ...settings.value.display,
      ...newSettings,
    }
  }

  /**
   * Сменить язык
   */
  const changeLanguage = (language: Language) => {
    settings.value.display.language = language
  }

  /**
   * Сменить тему.
   *
   * Применение отдано пакету дизайн-системы (атрибут data-theme, подписка на
   * системную тему при AUTO), хранение осталось здесь. Значение пишем сами, а
   * не полагаемся на обратный вызов persist: тот подключается только в
   * initializeSettings, и до него смена темы молча не сохранялась бы.
   */
  const changeTheme = (theme: Theme) => {
    settings.value.display.theme = theme
    setPreference(theme)
  }

  // --- Онбординг первого входа (переиспользуемо по featureId) ---

  /** Просмотрен ли онбординг раздела. */
  const isOnboardingSeen = (featureId: string): boolean =>
    !!settings.value.display.onboardingSeen?.[featureId]

  /** Отметить онбординг раздела просмотренным. */
  const markOnboardingSeen = (featureId: string) => {
    settings.value.display = {
      ...settings.value.display,
      onboardingSeen: { ...(settings.value.display.onboardingSeen ?? {}), [featureId]: true },
    }
  }

  /** Сбросить флаг онбординга раздела (показать снова). */
  const resetOnboarding = (featureId: string) => {
    const next = { ...(settings.value.display.onboardingSeen ?? {}) }
    delete next[featureId]
    settings.value.display = { ...settings.value.display, onboardingSeen: next }
  }

  /**
   * Сбросить настройки к дефолтным
   */
  const resetSettings = () => {
    settings.value = JSON.parse(JSON.stringify(DEFAULT_SETTINGS))
  }

  /**
   * Инициализация настроек.
   *
   * Тему ведёт пакет дизайн-системы, хранение остаётся здесь: persist пишет
   * выбор в стор, а watch выше кладёт его в localStorage вместе с остальными
   * настройками — отдельного ключа под тему не заводим.
   */
  const initializeSettings = () => {
    setupTheme({
      initial: settings.value.display.theme,
      persist: (theme) => {
        settings.value.display.theme = theme as Theme
      },
    })
  }

  return {
    // State
    settings,
    isLoading,

    // Actions
    updateNotificationSettings,
    updateDisplaySettings,
    changeLanguage,
    changeTheme,
    isOnboardingSeen,
    markOnboardingSeen,
    resetOnboarding,
    resetSettings,
    initializeSettings,
  }
})
